import { useEffect, useState } from "react";
import { isMasked } from "../../utils/maskedValue";
import {
  Typography,
  Card,
  Tabs,
  Tag,
  message,
} from "antd";

import { useLocation } from "react-router";
import {
  canManageProjectReviewItems,
  getCurrentUserRole,
} from "../../config/roleAccess";
import { reviewCommentsAndActionsService } from "../../services/reviewCommentsAndActionsService";
import { wsrService } from "../../services/wsrService";
import ProjectHealthStatus from "../../components/ProjectHealthStatus";
import DataState from "../../components/DataState/DataState";
import { LeadershipAttentionSection } from "./ReviewDetails/LeadershipAttentionSection";
import { CommentsActionsTab } from "./ReviewDetails/CommentsActionsTab";
import { WsrReportTab } from "./ReviewDetails/WsrReportTab";
import { ValueAddsTab } from "./ReviewDetails/ValueAddsTab";

import { createFallbackProjectMeta, DEFAULT_PROJECT_ID } from "./ReviewDetails/projectReview.utils";
import {
  useAddValueAddsMutation,
  useDeleteValueAddMutation,
  useProjectHealthSearchQuery,
  useUpdateValueAddMutation,
  useValueAddsQuery,
} from "./ReviewDetails/projectReview.hooks";
import { ProjectFilesUpload } from "./ReviewDetails/ProjectFilesUpload";
import { useAppSelector } from "../../store/hooks";

// ============= Main Component =============

export function ProjectReview() {
  const location = useLocation();
  const selectedProjectFromRoute =
    (location.state )?.project;
  const selectedProjectId = selectedProjectFromRoute?.id || DEFAULT_PROJECT_ID;
const selectedcustomerName = selectedProjectFromRoute?.department || DEFAULT_PROJECT_ID;
  const [selectedProjectMetaState, setSelectedProjectMetaState] = useState(
    selectedProjectFromRoute ?? createFallbackProjectMeta(selectedProjectId),
  );

  const lastReviewDate = useAppSelector(
    (state) =>
        state.projectHealthStatus
            ?.byProjectId?.[String(selectedProjectId)]
            ?.lastReviewDate ?? null
);

  useEffect(() => {
    if (selectedProjectFromRoute) {
      setSelectedProjectMetaState((prev) => ({
        ...prev,
        ...selectedProjectFromRoute,
      }));
    }
  }, [selectedProjectFromRoute]);

  const selectedProjectMeta = selectedProjectMetaState;

  const currentUserRole = getCurrentUserRole();
  const canManageReviewItems = canManageProjectReviewItems(currentUserRole);

  const [activeTab, setActiveTab] = useState("leadership-attention");
  // If navigation provided a `tab` in route state, honor it (e.g. leadership-attention)
  useEffect(() => {
    const routeTab = (location.state )?.tab;
    if (routeTab) setActiveTab(String(routeTab));
  }, [location.state]);

  // Fetch project-health details when the route only carried an id (fallback metadata is displayed),
  // or when the Leadership Attention tab is opened directly (e.g. from the dashboard bell).
  const shouldFetchHealth =
    !selectedProjectMetaState.name ||
    selectedProjectMetaState.name === "Project" ||
    activeTab === "leadership-attention";
  const healthSearchQuery = useProjectHealthSearchQuery(selectedProjectId, shouldFetchHealth);

  useEffect(() => {
    if (!healthSearchQuery.data) return;

    setSelectedProjectMetaState((prev) => ({
      ...(prev || createFallbackProjectMeta(selectedProjectId)),
      ...(selectedProjectFromRoute || {}),
      ...healthSearchQuery.data,
    }));
  }, [healthSearchQuery.data, selectedProjectFromRoute, selectedProjectId]);

  // Project whose comments/actions have finished loading (success or failure). Loading until it matches the selected project.
  const [commentsLoadedFor, setCommentsLoadedFor] = useState(null);
  // Header status: loading while the health lookup (when it runs) or the comments/actions load is in flight.
  const healthFailed = shouldFetchHealth && healthSearchQuery.isError && !healthSearchQuery.data;
  const healthPending = shouldFetchHealth && healthSearchQuery.isPending;
  const commentsPending = Boolean(selectedProjectId) && commentsLoadedFor !== selectedProjectId;
  const pageStatus = healthFailed ? "error" : healthPending || commentsPending ? "loading" : "ready";
  const pageError = healthFailed ? healthSearchQuery.error?.message || "Failed to load project details" : "";
  const handleRetryReview = () => {
    void healthSearchQuery.refetch();
  };

  const [comments, setComments] = useState([]);
  const [actions, setActions] = useState([]);
  const [commentText, setCommentText] = useState("");
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editingCommentText, setEditingCommentText] = useState("");
  const [actionItemsText, setActionItemsText] = useState("");
  const [editingRowId, setEditingRowId] = useState(null);
  const [valueAddText, setValueAddText] = useState("");
  const [editingValueAddId, setEditingValueAddId] = useState(null);
  const [editingValueAddForm, setEditingValueAddForm] = useState({
    title: "",
    category: "",
    description: "",
    owner: "",
  });
  const [editingRowData, setEditingRowData] = useState({
    description: "",
    owner: "",
    dueDate: "",
    priority: "Medium",
    status: "Open",
  });

  const [wsrFiles, setWsrFiles] = useState([]);
  const [wsrLoading, setWsrLoading] = useState(false);
  const [wsrUploading, setWsrUploading] = useState(false);

  useEffect(() => {
    setCommentText("");
    setEditingCommentId(null);
    setEditingCommentText("");
    setActionItemsText("");
    setEditingRowId(null);
    setValueAddText("");
    setEditingValueAddId(null);
    setEditingValueAddForm({ title: "", category: "", description: "", owner: "" });
  }, [selectedProjectId, selectedProjectMeta]);

useEffect(() => {
  if (!selectedProjectId) return;

  // Set when the project changes so a superseded response cannot overwrite newer state.
  let ignore = false;

  const loadCommentsAndActions = async () => {
    try {
      const [commentsResponse, actionsResponse] = await Promise.all([
        reviewCommentsAndActionsService.getComments(selectedProjectId),
        reviewCommentsAndActionsService.getActions(selectedProjectId),
      ]);

      if (!ignore) {
        setComments(commentsResponse || []);
        setActions(actionsResponse || []);
      }
    } catch (error) {
      // Review may have been deleted — show empty state instead of an error
      console.error(error);
      if (!ignore) {
        setComments([]);
        setActions([]);
      }
    } finally {
      if (!ignore) setCommentsLoadedFor(selectedProjectId);
    }
  };

  loadCommentsAndActions();

  return () => {
    ignore = true;
  };
}, [selectedProjectId]);


  // Fetch WSR files from API when tab is opened or project changes
  useEffect(() => {
    if (activeTab !== "war-status-report" || !selectedProjectId) return;

    const fetchWsrFiles = async () => {
      setWsrLoading(true);
      try {
        const files = await wsrService.listWsrFiles(selectedProjectId);
        setWsrFiles(files);
      } catch (error) {
        console.error("Error fetching WSR files:", error);
        setWsrFiles([]);
      } finally {
        setWsrLoading(false);
      }
    };

    fetchWsrFiles();
  }, [activeTab, selectedProjectId]);

  const valueAddsQuery = useValueAddsQuery(selectedProjectMeta.id, selectedProjectMeta.pm, activeTab === "value-adds");
  const valueAddItems = valueAddsQuery.data?.items ?? [];
  const valueAddTotalCount = valueAddsQuery.data?.totalCount ?? 0;
  const valueAddsLoading = valueAddsQuery.isLoading;

  const addValueAddsMutation = useAddValueAddsMutation(selectedProjectMeta.id);
  const updateValueAddMutation = useUpdateValueAddMutation(selectedProjectMeta.id);
  const deleteValueAddMutation = useDeleteValueAddMutation(selectedProjectMeta.id);

  const handleAddComment = async () => {
    if (!selectedProjectId) return;

    if (!commentText.trim()) {
      message.warning("Please enter a comment");
      return;
    }

    try {
      const lines = commentText
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line);

      await reviewCommentsAndActionsService.addComment(
        selectedProjectId,
        {
          comments: lines,
        }
      );

      const commentsResponse =
        await reviewCommentsAndActionsService.getComments(
          selectedProjectId
        );

      setComments(commentsResponse || []);
      setCommentText("");

      message.success("Comment added!");
    } catch (error) {
      console.error(error);
      message.error("Failed to add comment");
    }
  };

  const handleEditComment = (id) => {
    const comment = comments.find(
      (c) => c.commentId === id
    );

    if (comment) {
      setEditingCommentId(id);
      setEditingCommentText(comment.comment);
    }
  };
  const handleSaveCommentEdit = async () => {
    if (!selectedProjectId || !editingCommentId) return;

    if (!editingCommentText.trim()) {
      message.warning("Comment cannot be empty");
      return;
    }

    try {
      await reviewCommentsAndActionsService.updateComment(
        selectedProjectId,
        editingCommentId,
        {
          comment: editingCommentText,
        }
      );

      const commentsResponse =
        await reviewCommentsAndActionsService.getComments(
          selectedProjectId
        );

      setComments(commentsResponse || []);

      setEditingCommentId(null);
      setEditingCommentText("");

      message.success("Comment updated!");
    } catch (error) {
      console.error(error);
      message.error("Failed to update comment");
    }
  };

  const handleCancelCommentEdit = () => {
    setEditingCommentId(null);
    setEditingCommentText("");
  };
  const handleDeleteComment = async (id) => {
    if (!selectedProjectId) return;

    try {
      await reviewCommentsAndActionsService.deleteComment(
        selectedProjectId,
        id
      );

      const commentsResponse =
        await reviewCommentsAndActionsService.getComments(
          selectedProjectId
        );

      setComments(commentsResponse || []);

      message.success("Comment deleted!");
    } catch (error) {
      console.error(error);
      message.error("Failed to delete comment");
    }
  };

  const handleAddAction = async () => {
    if (!selectedProjectId) return;

    if (!actionItemsText.trim()) {
      message.warning("Please enter action items");
      return;
    }

    try {
      const lines = actionItemsText
        .split("\n")
        .map((line) => line.trim())
        .filter((line) => line);

      if (lines.length === 0) return;

      await reviewCommentsAndActionsService.addAction(
        selectedProjectId,
        {
          actionItems: lines,
        }
      );

      const actionsResponse =
        await reviewCommentsAndActionsService.getActions(
          selectedProjectId
        );

      setActions(actionsResponse || []);

      setActionItemsText("");

      message.success(
        `${lines.length} action item${lines.length > 1 ? "s" : ""} added!`
      );
    } catch (error) {
      console.error(error);
      message.error("Failed to add action item");
    }
  };

  const handleEditAction = (id) => {
    const action = actions.find(
      (a) => a.actionId === id
    );

    if (action) {
      setEditingRowId(id);

      setEditingRowData({
        description: action.actionItem,
        owner: action.owner || "",
        dueDate: action.dueDate || "",
        priority: action.priority || "NA",
        status: action.status || "NA",
      });
    }
  };

  const handleSaveActionEdit = async () => {
    if (!selectedProjectId || !editingRowId) return;

    if (!editingRowData.description.trim()) {
      message.warning("Please enter an action description");
      return;
    }

    try {
      await reviewCommentsAndActionsService.updateAction(
        selectedProjectId,
        editingRowId,
        {
          actionItem: editingRowData.description,
          owner: editingRowData.owner,
          dueDate: editingRowData.dueDate,
          priority: editingRowData.priority,
          status: editingRowData.status || "NA",
          isDelete: false,
        }
      );

      const actionsResponse =
        await reviewCommentsAndActionsService.getActions(
          selectedProjectId
        );

      setActions(actionsResponse || []);

      setEditingRowId(null);

      message.success("Action updated!");
    } catch (error) {
      console.error(error);
      message.error("Failed to update action");
    }
  };

  const handleUpdateEditingRowField = (
    field,
    value,
  ) => {
    setEditingRowData((prev) => ({ ...prev, [field]: value }));
  };

  const handleCancelActionEdit = () => {
    setEditingRowId(null);
    setEditingRowData({
      description: "",
      owner: "",
      dueDate: "",
      priority: "Medium",
      status: "Open",
    });
  };
  const handleDeleteAction = async (id) => {
    if (!selectedProjectId) return;

    try {
      await reviewCommentsAndActionsService.deleteAction(
        selectedProjectId,
        id
      );

      const actionsResponse =
        await reviewCommentsAndActionsService.getActions(
          selectedProjectId
        );

      setActions(actionsResponse || []);

      message.success("Action deleted!");
    } catch (error) {
      console.error(error);
      message.error("Failed to delete action");
    }
  };

  const handleAddValueAdd = async () => {
    if (!valueAddText.trim()) {
      message.warning("Please enter value add items");
      return;
    }

    const lines = valueAddText
      .split("\n")
      .map((line) => line.trim())
      .filter((line) => line);

    if (lines.length === 0) return;

    try {
      await addValueAddsMutation.mutateAsync(lines);
      setValueAddText("");
      message.success(`Added ${lines.length} value add item${lines.length > 1 ? "s" : ""}`);
    } catch (error) {
      console.error("Error saving value add items:", error);
      message.error("Failed to save value add items. Please try again.");
    }
  };

  const handleEditValueAdd = (item) => {
    setEditingValueAddId(item.id);
    setEditingValueAddForm({
      title: item.title,
      category: item.category,
      description: item.description,
      owner: item.owner,
    });
  };

  const handleSaveValueAddEdit = async () => {
    if (!selectedProjectMeta.id || !editingValueAddId) return;

    const existingItem = valueAddItems.find((item) => item.id === editingValueAddId);
    if (!existingItem) {
      message.error("Unable to find selected value add item");
      return;
    }

    const titleToSend = editingValueAddForm.title.trim() || existingItem.title;
    const descriptionToSend = editingValueAddForm.description;

    try {
      await updateValueAddMutation.mutateAsync({
        valueAddId: editingValueAddId,
        payload: { title: titleToSend, description: descriptionToSend },
      });
      setEditingValueAddId(null);
      setEditingValueAddForm({ title: "", category: "", description: "", owner: "" });
      message.success("Value add updated");
    } catch (error) {
      console.error("Error updating value add:", error);
      message.error("Failed to update value add. Please try again.");
    }
  };

  const handleCancelValueAddEdit = () => {
    setEditingValueAddId(null);
    setEditingValueAddForm({ title: "", category: "", description: "", owner: "" });
  };

  const handleDeleteValueAdd = async (valueAddId) => {
    if (!selectedProjectMeta.id || !valueAddId) return;

    try {
      await deleteValueAddMutation.mutateAsync(valueAddId);
      message.success("Value add deleted!");
    } catch (error) {
      console.error("Error deleting value add:", error);
      message.error("Failed to delete value add. Please try again.");
    }
  };

  const handleUploadWsrFile = async (file) => {
    if (!selectedProjectId) return false;

    setWsrUploading(true);
    try {
      await wsrService.uploadWsrFile(selectedProjectId, file);
      message.success(`${file.name} uploaded successfully`);

      const files = await wsrService.listWsrFiles(selectedProjectId);
      setWsrFiles(files);
    } catch (error) {
      console.error("Error uploading WSR file:", error);
      message.error("Failed to upload WSR file");
    } finally {
      setWsrUploading(false);
    }

    return false;
  };

  const handleDownloadWsrFile = async (file) => {
    if (!selectedProjectId) return;

    try {
      await wsrService.downloadWsrFile(
        selectedProjectId,
        file.fileId,
        file.fileName
      );
    } catch (error) {
      console.error("Error downloading WSR file:", error);
      message.error("Failed to download WSR file");
    }
  };

  const wsrUploadProps = {
    accept: ".ppt,.pptx,.pdf,.doc,.docx",
    multiple: false,
    showUploadList: false,
    disabled: wsrUploading,
    beforeUpload: (file) => {
      handleUploadWsrFile(file);
      return false;
    },
  };

  const sortedWsrFiles = [...wsrFiles].sort(
    (a, b) => new Date(b.uploadedDate).getTime() - new Date(a.uploadedDate).getTime()
  );
  const currentWsrFile = sortedWsrFiles[0] || null;
  const previousWsrFile = sortedWsrFiles[1] || null;
  const currentMonthLabel = "Current WSR";
  const previousMonthLabel = "Previous WSR";

  const healthStatusCard = (
    <ProjectHealthStatus projectId={selectedProjectId} readOnly={false} />
  );


  return (
    <div style={{ background: "linear-gradient(180deg,#f6f9ff 0%,#f9fbff 100%)", padding: 10, borderRadius: 16 }}>
      <DataState
        status={pageStatus}
        errorTitle="Couldn't load project review"
        error={pageError}
        onRetry={handleRetryReview}
        skeletonRows={6}
      >
      <Card
        style={{
          borderRadius: 16,
          border: "1px solid #e6ecf5",
          marginBottom: 16,
          boxShadow: "0 8px 24px rgba(16,24,40,0.04)",
          background: "linear-gradient(135deg,#ffffff 0%,#f8fbff 100%)",
        }}
        styles={{ body: { padding: 20 } }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 16,
          }}
        >
          {/* Row 1: Project ID tags + name + PM/DM */}
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 24, flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 320px", minWidth: 280 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  marginBottom: 10,
                  flexWrap: "wrap",
                }}
              >
                <Tag
                  style={{
                    margin: 0,
                    borderRadius: 999,
                    fontWeight: 600,
                    paddingInline: 10,
                  }}
                >
                  {selectedProjectMeta.id}
                </Tag>

                <Tag
                  color="blue"
                  style={{
                    margin: 0,
                    borderRadius: 999,
                    fontWeight: 600,
                    paddingInline: 10,
                  }}
                >
                  {selectedProjectMeta.contractCategory || "T & M"}
                </Tag>
              </div>

              <Typography.Title
                level={2}
                style={{
                  margin: 0,
                  marginBottom: 10,
                  color: "#3c3c3e",
                  letterSpacing: "-0.02em",
                }}
              >
                {selectedProjectMeta.name}
              </Typography.Title>

              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  gap: 16,
                  flexWrap: "wrap",
                }}
              >
                <Typography.Text style={{ color: "#334155" }}>
                  <strong>PM:</strong>{" "}
                  {Array.isArray(selectedProjectMeta.pm)
                    ? selectedProjectMeta.pm.join(", ")
                    : selectedProjectMeta.pm || "-"}
                </Typography.Text>

                <Typography.Text style={{ color: "#334155" }}>
                  <strong>DM:</strong>{" "}
                  {Array.isArray(selectedProjectMeta.sponsor)
                    ? selectedProjectMeta.sponsor.join(", ")
                    : selectedProjectMeta.sponsor || "-"}
                </Typography.Text>
              </div>
            </div>

            {/* BUDGET and COMPLETION cards only */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "flex-start",
              }}
            >
              <div
                style={{
                  border: "1px solid #e6ecf5",
                  borderRadius: 12,
                  padding: 12,
                  background: "#fff",
                  minWidth: 160,
                }}
              >
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700 }}
                >
                  Last Reviewed Date
                </Typography.Text>
                <div style={{ fontWeight: 500, color: "#3c3c3e", marginTop: 6, fontSize: 16 }}>
                  {lastReviewDate
                    ? lastReviewDate
                    : "—"}
                </div>
              </div>
              <div
                style={{
                  border: "1px solid #e6ecf5",
                  borderRadius: 12,
                  padding: 12,
                  background: "#fff",
                  minWidth: 160,
                }}
              >
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700 }}
                >
                  TCV
                </Typography.Text>
                <div style={{ fontWeight: 700, color: "#3c3c3e", marginTop: 6, fontSize: 16 }}>
                  {selectedProjectMeta.totalContractValue
                    ? isMasked(selectedProjectMeta.totalContractValue)
                      ? String(selectedProjectMeta.totalContractValue).trim()
                      : `$${Number(selectedProjectMeta.totalContractValue).toLocaleString()}`
                    : "—"}
                </div>
              </div>

              <div
                style={{
                  border: "1px solid #e6ecf5",
                  borderRadius: 12,
                  padding: 12,
                  background: "#fff",
                  minWidth: 160,
                }}
              >
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11, letterSpacing: "0.08em", fontWeight: 700 }}
                >
                  Milestone Completion
                </Typography.Text>
                <div style={{ fontWeight: 700, color: "#3c3c3e", marginTop: 6, fontSize: 16  }}>
                  {selectedProjectMeta.completedPercentage != null
                    ? `${selectedProjectMeta.completedPercentage}%`
                    : selectedProjectMeta.completion != null
                      ? `${selectedProjectMeta.completion}%`
                      : "—"}
                </div>
              </div>
            </div>
          </div>

          {/* Health status metrics inside the project details header */}
          {healthStatusCard}

        </div>
      </Card>

      <Tabs
        defaultActiveKey="leadership-attention"
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key)}
        tabBarStyle={{
          marginBottom: 14,
          background: "#ffffff",
          padding: "0 12px",
          borderRadius: 12,
          border: "1px solid #e6ecf5",
        }}
        items={[
                 {
            key: "leadership-attention",
            label: "Leadership Attention",
            children: (
              <LeadershipAttentionSection
                projectId={selectedProjectMeta.id}
                canManage={canManageReviewItems}
              />
            ),
          },
          {
            key: "comments-actions",
            label: "Comments & Actions",
            children: (
              <CommentsActionsTab
                canManageReviewItems={canManageReviewItems}
                selectedProjectId={selectedProjectId}
                commentText={commentText}
                actionItemsText={actionItemsText}
                onCommentTextChange={setCommentText}
                onActionItemsTextChange={setActionItemsText}
                onAddComment={handleAddComment}
                onAddAction={handleAddAction}
                comments={comments}
                actions={actions}
                editingCommentId={editingCommentId}
                editingCommentText={editingCommentText}
                onEditComment={handleEditComment}
                onEditCommentTextChange={setEditingCommentText}
                onSaveCommentEdit={handleSaveCommentEdit}
                onCancelCommentEdit={handleCancelCommentEdit}
                onDeleteComment={handleDeleteComment}
                editingRowId={editingRowId}
                editingRowData={editingRowData}
                onEditAction={handleEditAction}
                onUpdateEditingRowField={handleUpdateEditingRowField}
                onSaveActionEdit={handleSaveActionEdit}
                onCancelActionEdit={handleCancelActionEdit}
                onDeleteAction={handleDeleteAction}
              />
            ),
          },
                    {
  key: "war-status-report",
  label: "WSR Report",
  children: (
    <WsrReportTab
      selectedProjectId={selectedProjectId}
      selectedcustomerName={selectedcustomerName}
      wsrUploadProps={wsrUploadProps}
      wsrUploading={wsrUploading}
      currentMonthLabel={currentMonthLabel}
      previousMonthLabel={previousMonthLabel}
      currentWsrFile={currentWsrFile}
      previousWsrFile={previousWsrFile}
      wsrLoading={wsrLoading}
      onDownloadWsrFile={handleDownloadWsrFile}
    />
  ),
},
          {
            key: "value-adds",
            label: "Value Adds",
            children: (
              <ValueAddsTab
                valueAddTotalCount={valueAddTotalCount}
                valueAddText={valueAddText}
                onValueAddTextChange={setValueAddText}
                onAddValueAdd={handleAddValueAdd}
                onClearValueAdd={() => setValueAddText("")}
                editingValueAddId={editingValueAddId}
                editingValueAddForm={editingValueAddForm}
                onEditingValueAddFieldChange={(field, value) =>
                  setEditingValueAddForm((prev) => ({ ...prev, [field]: value }))
                }
                onSaveValueAddEdit={handleSaveValueAddEdit}
                onCancelValueAddEdit={handleCancelValueAddEdit}
                valueAddsLoading={valueAddsLoading}
                valueAddItems={valueAddItems}
                onEditValueAdd={handleEditValueAdd}
                onDeleteValueAdd={handleDeleteValueAdd}
              />
            ),
          },
          {
            key: "project-related-documents",
            label: "Project Related Documents",
            children: (
              <ProjectFilesUpload
                projectId={selectedProjectMeta.id}
                canUpload={canManageReviewItems}
              />
            ),
          },
        ]}
      />
      </DataState>
    </div>
  );
}
